import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import { Visibility, Delete } from '@mui/icons-material';
import SimpleDicomViewerDialog from '../../radiology/SimpleDicomViewerDialog';

interface ImagingReportDialogProps {
  open: boolean;
  onClose: () => void;
  imagingReport: string;
  setImagingReport: (report: string) => void;
  dicomFile: File | null;
  setDicomFile: (file: File | null) => void;
  patientInfo?: {
    name?: string;
    mrn?: string;
    dateOfBirth?: string;
  };
}

export const ImagingReportDialog: React.FC<ImagingReportDialogProps> = ({
  open,
  onClose,
  imagingReport,
  setImagingReport,
  dicomFile,
  setDicomFile,
  patientInfo = {}
}) => {
  const [showDicomViewer, setShowDicomViewer] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setDicomFile(file);
  };

  const handleRemoveFile = () => {
    setDicomFile(null);
  };

  const handleViewDicom = () => {
    setShowDicomViewer(true);
  };

  const isDicomFile = (file: File) => {
    return file.name.toLowerCase().endsWith('.dcm') || 
           file.type === 'application/dicom' ||
           file.type === 'image/dicom';
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Attach Imaging Report</DialogTitle>
        <DialogContent>
          <TextField
            label="Textual Report"
            value={imagingReport}
            onChange={e => setImagingReport(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              sx={{ mb: 2 }}
            >
              Upload DICOM File
              <input
                type="file"
                accept=".dcm,application/dicom,image/dicom"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            
            {dicomFile && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip 
                    label={dicomFile.name} 
                    variant="outlined"
                    icon={isDicomFile(dicomFile) ? <Visibility /> : undefined}
                  />
                  <Typography variant="caption" color="text.secondary">
                    ({(dicomFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                  
                  {isDicomFile(dicomFile) && (
                    <Tooltip title="View DICOM Image">
                      <IconButton 
                        size="small" 
                        onClick={handleViewDicom}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Tooltip title="Remove File">
                    <IconButton 
                      size="small" 
                      onClick={handleRemoveFile}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Stack>
                
                {isDicomFile(dicomFile) && (
                  <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                    ✓ Valid DICOM file detected
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {/* You can add a save/upload handler here if needed */}
        </DialogActions>
      </Dialog>

      {/* DICOM Viewer Dialog */}
      {dicomFile && isDicomFile(dicomFile) && (
        <SimpleDicomViewerDialog
          open={showDicomViewer}
          onClose={() => setShowDicomViewer(false)}
          dicomFile={dicomFile}
          patientInfo={patientInfo}
          studyInfo={{
            modality: 'Unknown',
            studyDate: new Date().toISOString().split('T')[0]
          }}
          title="DICOM Image Viewer"
        />
      )}
    </>
  );
}; 